import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useLocationStore } from '@/store/location';

// ---- Types ----

export type PromotionType = 'basic' | 'advanced';
export type ScheduleType = 'one_time' | 'recurring';
export type DiscountType = 'percentage' | 'fixed';
export type ProductScope = 'all' | 'specific';
export type CustomerTarget = 'everyone' | 'exclusive';
export type RuleGroup =
  | 'basic_include'
  | 'basic_exclude'
  | 'buy_include'
  | 'buy_exclude'
  | 'get_include'
  | 'get_exclude';
export type FilterType = 'product' | 'category' | 'supplier' | 'sku';

export interface PromotionProductRule {
  id?: string;
  promotion_id?: string;
  rule_group: RuleGroup;
  filter_type: FilterType;
  filter_value: string;
}

export interface PromotionLocation {
  id?: string;
  promotion_id?: string;
  location_id: string;
}

export interface PromotionCustomerGroup {
  id?: string;
  promotion_id?: string;
  customer_group_id?: string | null;
  promo_code?: string | null;
}

export interface RecurringConfig {
  days: number[]; // 0=Sunday, 6=Saturday
  start_time?: string; // "HH:MM"
  end_time?: string; // "HH:MM"
  all_day?: boolean;
}

export interface AdvancedBuyCondition {
  trigger: 'buys_items' | 'spends_amount';
  quantity?: number;
  min_quantity?: number;
  max_quantity?: number;
  spend_amount?: number;
  product_scope: ProductScope;
}

export interface AdvancedGetReward {
  reward_type: 'free' | 'percentage' | 'fixed';
  discount_value?: number;
  quantity?: number;
  product_scope: ProductScope;
}

export interface AdvancedConfig {
  buy_condition: AdvancedBuyCondition;
  get_reward: AdvancedGetReward;
}

export interface Promotion {
  id: string;
  organization_id: string | null;
  location_id: string | null;
  name: string;
  description: string | null;
  promotion_type: PromotionType | null;
  schedule_type: ScheduleType | null;
  discount_type: DiscountType | null;
  discount_value: number | null;
  product_scope: ProductScope | null;
  customer_target: CustomerTarget | null;
  allow_loyalty: boolean;
  show_pos_prompt: boolean;
  pos_prompt_message: string | null;
  recurring_config: RecurringConfig | null;
  advanced_config: AdvancedConfig | null;
  priority: number;
  is_active: boolean;
  start_datetime: string | null;
  end_datetime: string | null;
  start_date: string | null;
  end_date: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  // Joined data
  promotion_locations?: PromotionLocation[];
  promotion_product_rules?: PromotionProductRule[];
  promotion_customer_groups?: PromotionCustomerGroup[];
}

export type PromotionTab = 'current' | 'past' | 'all';

export interface PromotionFilters {
  tab?: PromotionTab;
  search?: string;
  locationId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface CreatePromotionInput {
  name: string;
  description?: string;
  promotion_type: PromotionType;
  schedule_type: ScheduleType;
  discount_type?: DiscountType;
  discount_value?: number;
  product_scope?: ProductScope;
  customer_target?: CustomerTarget;
  allow_loyalty?: boolean;
  show_pos_prompt?: boolean;
  pos_prompt_message?: string;
  recurring_config?: RecurringConfig;
  advanced_config?: AdvancedConfig;
  start_datetime?: string;
  end_datetime?: string | null;
  is_active?: boolean;
  location_ids: string[];
  product_rules?: Omit<PromotionProductRule, 'id' | 'promotion_id'>[];
  customer_groups?: Omit<PromotionCustomerGroup, 'id' | 'promotion_id'>[];
}

// ---- Hooks ----

/**
 * Fetch promotions list with filtering by tab, search, outlet, and date
 */
export function usePromotions(filters: PromotionFilters = {}) {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: ['promotions', orgId, filters],
    queryFn: async () => {
      if (!orgId) return [];

      let query = supabase
        .from('promotions')
        .select(`
          *,
          promotion_locations(*),
          promotion_product_rules(*),
          promotion_customer_groups(*)
        `)
        .eq('organization_id', orgId)
        .order('created_at', { ascending: false });

      const now = new Date().toISOString();

      // Tab-based filtering
      if (filters.tab === 'current') {
        // Current and upcoming: active, not yet ended
        query = query
          .eq('is_active', true)
          .or(`end_datetime.is.null,end_datetime.gte.${now}`);
      } else if (filters.tab === 'past') {
        // Past: ended or inactive
        query = query.or(`is_active.eq.false,end_datetime.lt.${now}`);
      }
      // 'all' tab: no additional filter

      // Search by name
      if (filters.search) {
        query = query.ilike('name', `%${filters.search}%`);
      }

      // Filter by location
      if (filters.locationId) {
        // Get promotion ids that include this location
        const { data: locPromos } = await supabase
          .from('promotion_locations')
          .select('promotion_id')
          .eq('location_id', filters.locationId);

        if (locPromos && locPromos.length > 0) {
          const ids = locPromos.map((lp: any) => lp.promotion_id);
          query = query.in('id', ids);
        } else {
          return []; // No promotions for this location
        }
      }

      // Date range filter
      if (filters.dateFrom) {
        query = query.gte('start_datetime', filters.dateFrom);
      }
      if (filters.dateTo) {
        query = query.lte('start_datetime', filters.dateTo);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as Promotion[];
    },
    enabled: !!orgId,
  });
}

/**
 * Fetch a single promotion with all relations
 */
export function usePromotion(id: string | null) {
  return useQuery({
    queryKey: ['promotion', id],
    queryFn: async () => {
      if (!id) return null;

      const { data, error } = await supabase
        .from('promotions')
        .select(`
          *,
          promotion_locations(*),
          promotion_product_rules(*),
          promotion_customer_groups(*)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return data as Promotion;
    },
    enabled: !!id,
  });
}

/**
 * Create a new promotion with all related data
 */
export function useCreatePromotion() {
  const queryClient = useQueryClient();
  const { organization, profile } = useAuth();

  return useMutation({
    mutationFn: async (input: CreatePromotionInput) => {
      const orgId = organization?.id;
      if (!orgId) throw new Error('No organization');

      // Get max priority for ordering
      const { data: existing } = await supabase
        .from('promotions')
        .select('priority')
        .eq('organization_id', orgId)
        .order('priority', { ascending: false })
        .limit(1);

      const nextPriority = existing && existing.length > 0
        ? (existing[0] as any).priority + 1
        : 0;

      // Insert the promotion
      const { data: promotion, error } = await supabase
        .from('promotions')
        .insert({
          organization_id: orgId,
          name: input.name,
          description: input.description || null,
          promotion_type: input.promotion_type,
          schedule_type: input.schedule_type,
          discount_type: input.discount_type || null,
          discount_value: input.discount_value || null,
          product_scope: input.product_scope || 'all',
          customer_target: input.customer_target || 'everyone',
          allow_loyalty: input.allow_loyalty || false,
          show_pos_prompt: input.show_pos_prompt || false,
          pos_prompt_message: input.pos_prompt_message || null,
          recurring_config: input.recurring_config || null,
          advanced_config: input.advanced_config || null,
          start_datetime: input.start_datetime || null,
          end_datetime: input.end_datetime || null,
          is_active: input.is_active !== false,
          priority: nextPriority,
          created_by: profile?.id || null,
        } as any)
        .select()
        .single();

      if (error) throw error;
      const promotionId = (promotion as any).id;

      // Insert location associations
      if (input.location_ids.length > 0) {
        const { error: locError } = await supabase
          .from('promotion_locations')
          .insert(
            input.location_ids.map(lid => ({
              promotion_id: promotionId,
              location_id: lid,
            }))
          );
        if (locError) throw locError;
      }

      // Insert product rules
      if (input.product_rules && input.product_rules.length > 0) {
        const { error: rulesError } = await supabase
          .from('promotion_product_rules')
          .insert(
            input.product_rules.map(rule => ({
              promotion_id: promotionId,
              rule_group: rule.rule_group,
              filter_type: rule.filter_type,
              filter_value: rule.filter_value,
            }))
          );
        if (rulesError) throw rulesError;
      }

      // Insert customer groups
      if (input.customer_groups && input.customer_groups.length > 0) {
        const { error: cgError } = await supabase
          .from('promotion_customer_groups')
          .insert(
            input.customer_groups.map(cg => ({
              promotion_id: promotionId,
              customer_group_id: cg.customer_group_id || null,
              promo_code: cg.promo_code || null,
            }))
          );
        if (cgError) throw cgError;
      }

      return promotion as any as Promotion;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
    },
  });
}

/**
 * Update an existing promotion with all related data
 */
export function useUpdatePromotion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: CreatePromotionInput }) => {
      // Update the promotion record
      const { data: promotion, error } = await supabase
        .from('promotions')
        .update({
          name: input.name,
          description: input.description || null,
          promotion_type: input.promotion_type,
          schedule_type: input.schedule_type,
          discount_type: input.discount_type || null,
          discount_value: input.discount_value || null,
          product_scope: input.product_scope || 'all',
          customer_target: input.customer_target || 'everyone',
          allow_loyalty: input.allow_loyalty || false,
          show_pos_prompt: input.show_pos_prompt || false,
          pos_prompt_message: input.pos_prompt_message || null,
          recurring_config: input.recurring_config || null,
          advanced_config: input.advanced_config || null,
          start_datetime: input.start_datetime || null,
          end_datetime: input.end_datetime || null,
          is_active: input.is_active !== false,
          updated_at: new Date().toISOString(),
        } as any)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Replace location associations
      await supabase.from('promotion_locations').delete().eq('promotion_id', id);
      if (input.location_ids.length > 0) {
        await supabase.from('promotion_locations').insert(
          input.location_ids.map(lid => ({
            promotion_id: id,
            location_id: lid,
          }))
        );
      }

      // Replace product rules
      await supabase.from('promotion_product_rules').delete().eq('promotion_id', id);
      if (input.product_rules && input.product_rules.length > 0) {
        await supabase.from('promotion_product_rules').insert(
          input.product_rules.map(rule => ({
            promotion_id: id,
            rule_group: rule.rule_group,
            filter_type: rule.filter_type,
            filter_value: rule.filter_value,
          }))
        );
      }

      // Replace customer groups
      await supabase.from('promotion_customer_groups').delete().eq('promotion_id', id);
      if (input.customer_groups && input.customer_groups.length > 0) {
        await supabase.from('promotion_customer_groups').insert(
          input.customer_groups.map(cg => ({
            promotion_id: id,
            customer_group_id: cg.customer_group_id || null,
            promo_code: cg.promo_code || null,
          }))
        );
      }

      return promotion as any as Promotion;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      queryClient.invalidateQueries({ queryKey: ['promotion', variables.id] });
    },
  });
}

/**
 * Delete a promotion
 */
export function useDeletePromotion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      // Junction tables cascade on delete
      const { error } = await supabase
        .from('promotions')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
    },
  });
}

/**
 * Duplicate an existing promotion (creates a copy with "(Copy)" appended to name)
 */
export function useDuplicatePromotion() {
  const queryClient = useQueryClient();
  const { organization, profile } = useAuth();

  return useMutation({
    mutationFn: async (id: string) => {
      const orgId = organization?.id;
      if (!orgId) throw new Error('No organization');

      // Fetch the original with all relations
      const { data: original, error: fetchError } = await supabase
        .from('promotions')
        .select(`
          *,
          promotion_locations(*),
          promotion_product_rules(*),
          promotion_customer_groups(*)
        `)
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;
      if (!original) throw new Error('Promotion not found');

      const orig = original as any;

      // Get next priority
      const { data: existing } = await supabase
        .from('promotions')
        .select('priority')
        .eq('organization_id', orgId)
        .order('priority', { ascending: false })
        .limit(1);

      const nextPriority = existing && existing.length > 0
        ? (existing[0] as any).priority + 1
        : 0;

      // Create the duplicate
      const { data: dup, error: createError } = await supabase
        .from('promotions')
        .insert({
          organization_id: orgId,
          name: `${orig.name} (Copy)`,
          description: orig.description,
          promotion_type: orig.promotion_type,
          schedule_type: orig.schedule_type,
          discount_type: orig.discount_type,
          discount_value: orig.discount_value,
          product_scope: orig.product_scope,
          customer_target: orig.customer_target,
          allow_loyalty: orig.allow_loyalty,
          show_pos_prompt: orig.show_pos_prompt,
          pos_prompt_message: orig.pos_prompt_message,
          recurring_config: orig.recurring_config,
          advanced_config: orig.advanced_config,
          start_datetime: orig.start_datetime,
          end_datetime: orig.end_datetime,
          is_active: false, // Start as inactive so user can review
          priority: nextPriority,
          created_by: profile?.id || null,
        } as any)
        .select()
        .single();

      if (createError) throw createError;
      const dupId = (dup as any).id;

      // Copy locations
      const locations = orig.promotion_locations || [];
      if (locations.length > 0) {
        await supabase.from('promotion_locations').insert(
          locations.map((loc: any) => ({
            promotion_id: dupId,
            location_id: loc.location_id,
          }))
        );
      }

      // Copy product rules
      const rules = orig.promotion_product_rules || [];
      if (rules.length > 0) {
        await supabase.from('promotion_product_rules').insert(
          rules.map((rule: any) => ({
            promotion_id: dupId,
            rule_group: rule.rule_group,
            filter_type: rule.filter_type,
            filter_value: rule.filter_value,
          }))
        );
      }

      // Copy customer groups
      const groups = orig.promotion_customer_groups || [];
      if (groups.length > 0) {
        await supabase.from('promotion_customer_groups').insert(
          groups.map((cg: any) => ({
            promotion_id: dupId,
            customer_group_id: cg.customer_group_id,
            promo_code: cg.promo_code,
          }))
        );
      }

      return dup as any as Promotion;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
    },
  });
}

/**
 * End a promotion early by setting end_datetime to now or a specified date
 */
export function useEndPromotionEarly() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, endNow, newEndDate }: { id: string; endNow?: boolean; newEndDate?: string }) => {
      const endDatetime = endNow ? new Date().toISOString() : newEndDate;

      const updates: any = {
        end_datetime: endDatetime,
        updated_at: new Date().toISOString(),
      };

      // If ending now, also deactivate
      if (endNow) {
        updates.is_active = false;
      }

      const { data, error } = await supabase
        .from('promotions')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as any as Promotion;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      queryClient.invalidateQueries({ queryKey: ['promotion', variables.id] });
    },
  });
}

/**
 * Get the resolved product list for a promotion (for "View products" feature)
 */
export function usePromotionProducts(promotionId: string | null) {
  const { currentLocation } = useLocationStore();

  return useQuery({
    queryKey: ['promotion-products', promotionId, currentLocation?.id],
    queryFn: async () => {
      if (!promotionId || !currentLocation?.id) return [];

      // Get the product rules for this promotion
      const { data: rules, error: rulesError } = await supabase
        .from('promotion_product_rules')
        .select('*')
        .eq('promotion_id', promotionId);

      if (rulesError) throw rulesError;

      // Get the promotion itself to check product_scope
      const { data: promo, error: promoError } = await supabase
        .from('promotions')
        .select('product_scope, promotion_type')
        .eq('id', promotionId)
        .single();

      if (promoError) throw promoError;

      // If product_scope is 'all', return all active products at this location
      if ((promo as any).product_scope === 'all') {
        const { data: products, error } = await supabase
          .from('products')
          .select('id, name, sku, price, category_id, supplier_id, is_active')
          .eq('location_id', currentLocation.id)
          .eq('is_active', true)
          .order('name');

        if (error) throw error;
        return products || [];
      }

      // Otherwise, resolve the product rules
      const includeRules = (rules || []).filter((r: any) =>
        r.rule_group.endsWith('_include')
      );
      const excludeRules = (rules || []).filter((r: any) =>
        r.rule_group.endsWith('_exclude')
      );

      // Start with all products, then filter
      let query = supabase
        .from('products')
        .select('id, name, sku, price, category_id, supplier_id, is_active')
        .eq('location_id', currentLocation.id)
        .eq('is_active', true);

      // Apply inclusion filters (OR logic between rules)
      const productIds: string[] = [];
      const categoryIds: string[] = [];
      const supplierIds: string[] = [];
      const skus: string[] = [];

      for (const rule of includeRules) {
        const r = rule as any;
        switch (r.filter_type) {
          case 'product':
            productIds.push(r.filter_value);
            break;
          case 'category':
            categoryIds.push(r.filter_value);
            break;
          case 'supplier':
            supplierIds.push(r.filter_value);
            break;
          case 'sku':
            skus.push(r.filter_value);
            break;
        }
      }

      // Build an OR query for inclusion
      const orConditions: string[] = [];
      if (productIds.length > 0) orConditions.push(`id.in.(${productIds.join(',')})`);
      if (categoryIds.length > 0) orConditions.push(`category_id.in.(${categoryIds.join(',')})`);
      if (supplierIds.length > 0) orConditions.push(`supplier_id.in.(${supplierIds.join(',')})`);
      if (skus.length > 0) orConditions.push(`sku.in.(${skus.join(',')})`);

      if (orConditions.length > 0) {
        query = query.or(orConditions.join(','));
      }

      const { data: products, error } = await query.order('name');
      if (error) throw error;

      // Apply exclusion filters client-side
      let result = products || [];
      for (const rule of excludeRules) {
        const r = rule as any;
        switch (r.filter_type) {
          case 'product':
            result = result.filter((p: any) => p.id !== r.filter_value);
            break;
          case 'category':
            result = result.filter((p: any) => p.category_id !== r.filter_value);
            break;
          case 'supplier':
            result = result.filter((p: any) => p.supplier_id !== r.filter_value);
            break;
          case 'sku':
            result = result.filter((p: any) => p.sku !== r.filter_value);
            break;
        }
      }

      return result;
    },
    enabled: !!promotionId && !!currentLocation?.id,
  });
}
